// Code generated by apic; DO NOT EDIT.
import type { GraphQLOpMeta } from "../types";
import type { SampleContext } from "./shared";
import { resolveAuthMode, hasMTLSLeaf } from "./shared";
import { shellEscapeSQ } from "./curl";
import { goStringLit } from "./go";
import { pyStringLit } from "./python";
import { tsStringLit } from "./typescript";

/**
 * GraphQL sample context extends SampleContext with GraphQL-specific fields.
 * The query document and variables are passed explicitly because GraphQL
 * endpoints always POST to a fixed path (/graphql).
 */
export interface GraphQLSampleContext {
  /** Base URL of the service (e.g. "https://api.example.com"). */
  baseUrl: string;
  /** GraphQL query/mutation/subscription document string. */
  query: string;
  /** Variables to pass alongside the query. */
  variables?: Record<string, unknown>;
  /** Bearer token for jwt / cookie-bearer auth. */
  token?: string;
  /** API key for api_key auth. */
  apiKey?: string;
}

// ── auth header helpers (shared pattern, applied to GraphQL) ──────────────────

function applyAuthCurl(
  lines: string[],
  auth: GraphQLOpMeta["auth"],
  ctx: GraphQLSampleContext,
): void {
  const mode = resolveAuthMode(auth);
  const composite = auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(auth);
  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`  -H 'Authorization: Bearer ${shellEscapeSQ(tok)}' \\`);
    if (composite && mtlsLeaf) {
      lines.push(`  # also requires a client certificate (mTLS — set via --cert / --key)`);
    }
  } else if (mode === "api_key") {
    const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
    lines.push(`  -H 'X-API-Key: ${shellEscapeSQ(key)}' \\`);
    if (composite && mtlsLeaf) {
      lines.push(`  # also requires a client certificate (mTLS — set via --cert / --key)`);
    }
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`  # requires a client certificate — cannot be expressed as a header`);
    lines.push(`  # add: --cert /path/to/client.pem --key /path/to/client-key.pem \\`);
  }
}

// ── builders ──────────────────────────────────────────────────────────────────

/**
 * graphqlCurlSample returns a curl command for a GraphQL operation.
 *
 * Always POSTs to <baseUrl>/graphql with Content-Type: application/json.
 * The body is the standard {query, variables} envelope.
 * Auth follows the same rules as REST (Bearer/X-API-Key/cert comment).
 */
export function graphqlCurlSample(op: GraphQLOpMeta, ctx: GraphQLSampleContext): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/graphql`;
  const envelope: Record<string, unknown> = { query: ctx.query };
  if (ctx.variables !== undefined && Object.keys(ctx.variables).length > 0) {
    envelope["variables"] = ctx.variables;
  }
  const body = JSON.stringify(envelope);

  const lines: string[] = [];
  lines.push(`curl -s \\`);
  lines.push(`  -X POST \\`);
  lines.push(`  '${shellEscapeSQ(url)}' \\`);
  lines.push(`  -H 'Content-Type: application/json' \\`);
  lines.push(`  -H 'Accept: application/json' \\`);
  applyAuthCurl(lines, op.auth, ctx);
  lines.push(`  -d '${shellEscapeSQ(body)}'`);

  // Remove trailing ` \` from the last non-data line if body is already last.
  return lines.join("\n");
}

/**
 * graphqlGoSample returns a Go net/http snippet for a GraphQL operation.
 */
export function graphqlGoSample(op: GraphQLOpMeta, ctx: GraphQLSampleContext): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/graphql`;
  const envelope: Record<string, unknown> = { query: ctx.query };
  if (ctx.variables !== undefined && Object.keys(ctx.variables).length > 0) {
    envelope["variables"] = ctx.variables;
  }
  const bodyJSON = JSON.stringify(envelope, null, "\t");
  const escaped = bodyJSON.replace(/`/g, "` + \"`\" + `");

  const mode = resolveAuthMode(op.auth);
  const composite = op.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(op.auth);

  const lines: string[] = [];
  lines.push(`package main`);
  lines.push(``);
  lines.push(`import (`);
  lines.push(`\t"bytes"`);
  lines.push(`\t"fmt"`);
  lines.push(`\t"net/http"`);
  lines.push(`)`);
  lines.push(``);
  lines.push(`func main() {`);
  lines.push(`\tbody := []byte(\`` + escaped + `\`)`);
  lines.push(`\treq, _ := http.NewRequest("POST", ${goStringLit(url)}, bytes.NewReader(body))`);
  lines.push(`\treq.Header.Set("Content-Type", "application/json")`);
  lines.push(`\treq.Header.Set("Accept", "application/json")`);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`\treq.Header.Set("Authorization", ${goStringLit("Bearer " + tok)})`);
    if (composite && mtlsLeaf) {
      lines.push(`\t// also requires mTLS client certificate (configure via tls.Config)`);
    }
  } else if (mode === "api_key") {
    const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
    lines.push(`\treq.Header.Set("X-API-Key", ${goStringLit(key)})`);
    if (composite && mtlsLeaf) {
      lines.push(`\t// also requires mTLS client certificate (configure via tls.Config)`);
    }
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`\t// requires mTLS client certificate — configure via tls.Config`);
    lines.push(`\t//   cert, _ := tls.LoadX509KeyPair("client.pem", "client-key.pem")`);
    lines.push(`\t//   client := &http.Client{Transport: &http.Transport{TLSClientConfig: &tls.Config{Certificates: []tls.Certificate{cert}}}}`);
  }

  lines.push(``);
  lines.push(`\tclient := &http.Client{}`);
  lines.push(`\tresp, err := client.Do(req)`);
  lines.push(`\tif err != nil {`);
  lines.push(`\t\tpanic(err)`);
  lines.push(`\t}`);
  lines.push(`\tdefer resp.Body.Close()`);
  lines.push(`\tfmt.Println(resp.Status)`);
  lines.push(`}`);

  return lines.join("\n");
}

/**
 * graphqlPythonSample returns a Python requests snippet for a GraphQL operation.
 */
export function graphqlPythonSample(op: GraphQLOpMeta, ctx: GraphQLSampleContext): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/graphql`;
  const envelope: Record<string, unknown> = { query: ctx.query };
  if (ctx.variables !== undefined && Object.keys(ctx.variables).length > 0) {
    envelope["variables"] = ctx.variables;
  }

  const mode = resolveAuthMode(op.auth);
  const composite = op.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(op.auth);

  // Serialize the envelope as a JSON string and use json.loads() in the emitted
  // Python so booleans/null serialize correctly (JSON true/false/null → Python
  // True/False/None). pyStringLit produces a valid Python double-quoted string
  // literal because JSON and Python share the same basic escape sequences.
  const payloadLit = `json.loads(${pyStringLit(JSON.stringify(envelope))})`;

  const lines: string[] = [];
  lines.push(`import json`);
  lines.push(`import requests`);
  lines.push(``);
  lines.push(`url = ${pyStringLit(url)}`);
  lines.push(`headers = {"Content-Type": "application/json", "Accept": "application/json"}`);
  lines.push(`payload = ${payloadLit}`);
  lines.push(``);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`headers["Authorization"] = ${pyStringLit("Bearer " + tok)}`);
    if (composite && mtlsLeaf) {
      lines.push(`# also requires mTLS client certificate (pass cert=(...) to requests)`);
    }
  } else if (mode === "api_key") {
    const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
    lines.push(`headers["X-API-Key"] = ${pyStringLit(key)}`);
    if (composite && mtlsLeaf) {
      lines.push(`# also requires mTLS client certificate (pass cert=(...) to requests)`);
    }
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`# requires a client certificate`);
    lines.push(`# cert = ("/path/to/client.pem", "/path/to/client-key.pem")`);
    lines.push(``);
    lines.push(`resp = requests.post(url, headers=headers, json=payload, cert=cert)`);
    lines.push(`resp.raise_for_status()`);
    lines.push(`print(resp.json())`);
    return lines.join("\n");
  }

  lines.push(``);
  lines.push(`resp = requests.post(url, headers=headers, json=payload)`);
  lines.push(`resp.raise_for_status()`);
  lines.push(`print(resp.json())`);

  return lines.join("\n");
}

/**
 * graphqlTsSample returns a TypeScript snippet for a GraphQL operation.
 *
 * When op.methodName is set, uses the generated client method (preferred).
 * Falls back to a raw fetch POST of the {query, variables} envelope.
 */
export function graphqlTsSample(op: GraphQLOpMeta, ctx: GraphQLSampleContext): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/graphql`;
  const methodName = op.methodName;
  const mode = resolveAuthMode(op.auth);
  const composite = op.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(op.auth);

  const lines: string[] = [];

  if (methodName !== "") {
    // ── preferred path: generated client method ───────────────────────────
    lines.push(`import { createClient } from "./client"; // generated client`);
    lines.push(``);
    lines.push(`async function main() {`);

    const clientOpts: string[] = [`baseUrl: ${tsStringLit(ctx.baseUrl)}`];

    if (mode === "jwt" || mode === "cookie") {
      const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
      clientOpts.push(`bearerToken: ${tsStringLit(tok)}`);
      if (composite && mtlsLeaf) {
        lines.push(`  // also requires mTLS client certificate`);
      }
    } else if (mode === "api_key") {
      lines.push(`  // api_key auth: supply an apiKeySigner in ClientConfig`);
      if (composite && mtlsLeaf) {
        lines.push(`  // also requires mTLS client certificate`);
      }
    } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`  // mTLS: configure a client certificate on the fetch transport`);
    }

    lines.push(`  const client = createClient({ ${clientOpts.join(", ")} });`);
    lines.push(``);

    if (ctx.variables !== undefined && Object.keys(ctx.variables).length > 0) {
      lines.push(`  const variables = ${JSON.stringify(ctx.variables)};`);
      lines.push(`  const result = await client.${methodName}(variables);`);
    } else {
      lines.push(`  const result = await client.${methodName}({});`);
    }

    lines.push(`  console.log(result);`);
    lines.push(`}`);
    lines.push(``);
    lines.push(`main().catch(console.error);`);
  } else {
    // ── fallback: raw fetch POST ──────────────────────────────────────────
    lines.push(`async function main() {`);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };

    if (mode === "jwt" || mode === "cookie") {
      const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
      headers["Authorization"] = `Bearer ${tok}`;
    } else if (mode === "api_key") {
      const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
      headers["X-API-Key"] = key;
    }

    const envelope: Record<string, unknown> = { query: ctx.query };
    if (ctx.variables !== undefined && Object.keys(ctx.variables).length > 0) {
      envelope["variables"] = ctx.variables;
    }

    lines.push(`  const headers: Record<string, string> = ${JSON.stringify(headers, null, 4).replace(/\n/g, "\n  ")};`);

    if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`  // mTLS: configure a client certificate on the fetch transport`);
    }
    if (composite && mtlsLeaf) {
      lines.push(`  // also requires mTLS client certificate`);
    }

    lines.push(``);
    lines.push(`  const resp = await fetch(${tsStringLit(url)}, {`);
    lines.push(`    method: "POST",`);
    lines.push(`    headers,`);
    lines.push(`    body: JSON.stringify(${JSON.stringify(envelope)}),`);
    lines.push(`  });`);
    lines.push(`  const data: unknown = await resp.json();`);
    lines.push(`  console.log(data);`);
    lines.push(`}`);
    lines.push(``);
    lines.push(`main().catch(console.error);`);
  }

  return lines.join("\n");
}

// Re-export SampleContext under the same name for convenience.
export type { SampleContext } from "./shared";
